<div class="npdc-title">
  @if (dataset(); as dataset) {
    <h1>Edit dataset - {{ dataset.content.title }}</h1>
  } @else {
    <h1>New dataset</h1>
  }

  <div class="npdc-title__actions">
    @if (!metadataForm().dirty()) {
      <a mat-flat-button type="button" routerLink="/dataset/{{ dataset()?.id }}">Close</a>
    } @else {
      <a mat-button type="button" routerLink="/dataset/{{ dataset()?.id }}">Close</a>
    }
  </div>
</div>

<mat-tab-group animationDuration="0ms" disablePagination="true">
  <mat-tab>
    <ng-template mat-tab-label>
      <mat-icon>info</mat-icon>
      General
    </ng-template>

    <form (submit)="save($event)">
      <p>
        <mat-form-field appearance="outline">
          <mat-label>Title</mat-label>
          <input matInput [formField]="metadataForm.title" />
        </mat-form-field>
      </p>

      <p>
        <app-form-control-markdown label="Summary" [formField]="metadataForm.summary" />
      </p>

      <p>
        <app-form-control-autocomplete
          label="Keywords"
          (searchString)="keywordSearch.set($event)"
          [resource]="keywordResource"
          [multiple]="true"
          [formField]="metadataForm.keywords"
        />
      </p>

      <p class="npdc-form-actions">
        <button
          mat-flat-button
          type="submit"
          [disabled]="metadataForm().invalid() || !metadataForm().dirty()"
        >
          Save
        </button>
      </p>
    </form>
  </mat-tab>

  <mat-tab [disabled]="!dataset() || !metadataForm().valid()">
    <ng-template mat-tab-label>
      <mat-icon>label</mat-icon>
      Labels
    </ng-template>

    <p>
      Selecting harvesters will flag the dataset for exposure in the associated thematic data
      portals. For exposure through search engines and generic data portals no further action is
      required.
    </p>

    <p>
      @if (labelsList.selectedOptions.selected.length; as length) {
        <button mat-button (click)="removeLabels(labelsList.selectedOptions)">Remove</button>
      } @else {
        <button mat-flat-button (click)="addLabels()">Add</button>
      }
    </p>

    <app-list [resource]="labels" [disableClear]="true">
      <mat-selection-list #labelsList>
        @if (!labels.error()) {
          @if (harvesterLabels().length) {
            <div mat-subheader>Harvesters</div>
            @for (label of harvesterLabels(); track label.id) {
              <mat-list-option togglePosition="before" [value]="label" cdkDrag>
                <span matListItemTitle>{{ label.title }}</span>
                <span matListItemLine>{{ label.type }}</span>
              </mat-list-option>
            }

            @if (projectLabels().length) {
              <mat-divider></mat-divider>
              <div mat-subheader>Projects</div>
            }
          }
          @for (label of projectLabels(); track label.id) {
            <mat-list-option togglePosition="before" [value]="label" cdkDrag>
              <span matListItemTitle>{{ label.title }}</span>
              <span matListItemLine>{{ label.type }}</span>
            </mat-list-option>
          }
        }
      </mat-selection-list>
    </app-list>
  </mat-tab>

  <mat-tab [disabled]="!dataset() || !metadataForm().valid()">
    <ng-template mat-tab-label>
      <mat-icon>public</mat-icon>
      Geographical coverage
    </ng-template>
    <ng-template matTabContent>
      <p>
        <mat-card class="npdc-leaflet" appearance="outlined">
          <app-leaflet-group
            [geometry]="dataset()?.content?.geojson"
            [readonly]="false"
            [height]="'600px'"
            (geometryChange)="saveGeoJSON($event)"
          ></app-leaflet-group>
        </mat-card>
      </p>

      <p>
        Switch between map projections and use the interactive map controls to mark areas of
        relevance. Changes will be saved automatically.
      </p>

      <p>The <b>T</b> tool can be used to add precise geometries.</p>
    </ng-template>
  </mat-tab>

  <mat-tab [disabled]="!dataset() || !metadataForm().valid()">
    <ng-template mat-tab-label>
      <mat-icon>schedule</mat-icon>
      Timeframes
    </ng-template>

    <p>This list supports drag-and-drop reordering.</p>

    <ng-container
      [ngTemplateOutlet]="listActionsTemplate"
      [ngTemplateOutletContext]="{ type: 'timeframes', list: list }"
    />

    <mat-selection-list #list cdkDropList (cdkDropListDropped)="drop('timeframes', $event)">
      @for (element of dataset()?.content?.timeframes; track track(element); let isLast = $last) {
        <mat-list-option togglePosition="before" [value]="element" cdkDrag>
          <span matListItemTitle><b>Start</b>: {{ element.startDate | date }}</span>
          @if (element.endDate) {
            <span matListItemLine><b>End</b>: {{ element.endDate | date }}</span>
          } @else {
            <span matListItemLine>Ongoing</span>
          }
        </mat-list-option>
      }
    </mat-selection-list>
  </mat-tab>

  <mat-tab [disabled]="!dataset() || !metadataForm().valid()">
    <ng-template mat-tab-label>
      <mat-icon>person</mat-icon>
      Contributors
    </ng-template>

    <p>This list supports drag-and-drop reordering.</p>

    <ng-container
      [ngTemplateOutlet]="listActionsTemplate"
      [ngTemplateOutletContext]="{ type: 'people', list: peopleList }"
    />
    <mat-selection-list #peopleList cdkDropList (cdkDropListDropped)="drop('people', $event)">
      @for (element of dataset()?.content?.people; track track(element); let isLast = $last) {
        <mat-list-option togglePosition="before" [value]="element" cdkDrag>
          <span matListItemTitle>{{ element.firstName }} {{ element.lastName }}</span>
          <span matListItemLine><b>Roles</b>: {{ element.roles | roleListString }}</span>
          <span matListItemLine>
            <span><b>Organisation</b>: {{ element.organisation || 'N/A' }}, </span>
            <span
              ><b>email</b>:
              @if (element.email) {
                <a href="mailto:{{ element.email }" target="_blank" appStopPropagation>{{
                  element.email
                }}</a>
              } @else {
                <span>N/A</span>
              }
              <span>, </span>
            </span>
            <span><b>ORCID</b>: {{ element.orcid || 'N/A' }}, </span>
            <span
              ><b>URL</b>:
              @if (element.url) {
                <a appStopPropagation href="{{ element.url }}" target="_blank" rel="nofollow">{{
                  element.url
                }}</a>
              } @else {
                <span>N/A</span>
              }
            </span>
          </span>
        </mat-list-option>
      }
    </mat-selection-list>
  </mat-tab>

  <mat-tab [disabled]="!dataset() || !metadataForm().valid()">
    <ng-template mat-tab-label>
      <mat-icon>business</mat-icon>
      Organisations
    </ng-template>
    <p>This list supports drag-and-drop reordering.</p>
    <ng-container
      [ngTemplateOutlet]="listActionsTemplate"
      [ngTemplateOutletContext]="{
        type: 'organisations',
        list: organisationsList,
      }"
    />
    <mat-selection-list
      #organisationsList
      cdkDropList
      (cdkDropListDropped)="drop('organisations', $event)"
    >
      @for (
        element of dataset()?.content?.organisations;
        track track(element);
        let isLast = $last
      ) {
        <mat-list-option togglePosition="before" [value]="element" cdkDrag>
          <span matListItemTitle>{{ element.name }}</span>
          <span matListItemLine><b>Roles</b>: {{ element.roles | roleListString }}</span>
          <span matListItemLine>
            <span
              ><b>URL</b>:
              @if (element.url) {
                <a appStopPropagation href="{{ element.url }}" target="_blank" rel="nofollow">{{
                  element.url
                }}</a>
              } @else {
                <span>N/A</span>
              }
            </span>
            <span>, </span>
            <span
              ><b>email</b>:
              @if (element.email) {
                <a appStopPropagation href="mailto:{{ element.email }" target="_blank">{{
                  element.email
                }}</a>
              } @else {
                <span>N/A</span>
              }
            </span>
          </span>
        </mat-list-option>
      }
    </mat-selection-list>
  </mat-tab>

  <mat-tab [disabled]="!dataset() || !metadataForm().valid()">
    <ng-template mat-tab-label>
      <mat-icon>link</mat-icon>
      Links
    </ng-template>
    <p>This list supports drag-and-drop reordering.</p>
    <ng-container
      [ngTemplateOutlet]="listActionsTemplate"
      [ngTemplateOutletContext]="{ type: 'links', list: linksList }"
    />
    <mat-selection-list #linksList cdkDropList (cdkDropListDropped)="drop('links', $event)">
      @for (element of dataset()?.content?.links; track track(element); let isLast = $last) {
        <mat-list-option togglePosition="before" [value]="element" cdkDrag>
          <span matListItemTitle
            ><a appStopPropagation href="{{ element.href }}" target="_blank" rel="nofollow">{{
              element.title || element.href
            }}</a></span
          >
          <span matListItemLine>{{ element.rel | titlecase }}</span>
        </mat-list-option>
      }
    </mat-selection-list>
  </mat-tab>

  <mat-tab [disabled]="!dataset() || !metadataForm().valid()">
    <ng-template mat-tab-label>
      <mat-icon>attach_file</mat-icon>
      Files
    </ng-template>

    <p>Files can only be added or removed while a dataset is in <strong>Draft</strong> state. To update files on a published dataset, create a new version.</p>

    <p>
      <input
        #fileInput
        type="file"
        title="blob"
        name="blob"
        placeholder="Select one or more files to upload"
        multiple
        hidden
        (change)="createFiles($event)"
      />
      <input
        #dirInput
        type="file"
        title="blob"
        name="blob"
        placeholder="Select a directory to upload"
        webkitdirectory
        multiple
        hidden
        (change)="createFiles($event)"
      />

      @if (filesList.selectedOptions.selected.length; as length) {
        <button mat-flat-button (click)="updateFiles(filesList.selectedOptions.selected)">
          Edit
        </button>
        <button mat-button (click)="deleteFiles(filesList.selectedOptions)" [disabled]="!mayAddFiles()">Delete</button>
      } @else {
        <button mat-flat-button (click)="fileInput.click()" [disabled]="!mayAddFiles()">Add</button>
        <button mat-button (click)="dirInput.click()" [disabled]="!mayAddFiles()">Add directory</button>
      }
    </p>
    <app-list [resource]="files" [disableClear]="true">
      <mat-selection-list #filesList>
        @if (!files.error()) {
          @for (attachment of files.value()?.items; track attachment.id) {
            <mat-list-option togglePosition="before" [value]="attachment.id">
              <span matListItemTitle>@if (attachment.type === 'symlink') {(inherited) }{{ attachment.title || attachment.filename }}</span>

              <span matListItemLine>
                <strong>Prefix:</strong>
                {{ attachment.prefix }}
              </span>

              <span matListItemLine>
                @if (attachment.released) {
                  <strong>Released:</strong>
                  {{ attachment.released | date }}
                }
                <strong>Size:</strong>
                {{ attachment.byteSize | humanReadableSize }}
              </span>
            </mat-list-option>
          }
        }
      </mat-selection-list>

      @if (!files.error()) {
        <mat-paginator
          showFirstLastButtons
          (page)="filesPage.set($event.pageIndex); filesPageSize.set($event.pageSize)"
          [length]="files.value()?.count"
          [pageIndex]="filesPage()"
          [pageSize]="filesPageSize()"
          [pageSizeOptions]="[10, 20, 50, 100]"
        >
        </mat-paginator>
      }
    </app-list>
  </mat-tab>
</mat-tab-group>

<ng-template #listActionsTemplate let-type="type" let-list="list">
  <p>
    @if (list.selectedOptions.selected.length; as length) {
      @if (length === 1) {
        <button mat-flat-button (click)="open(type, list.selectedOptions.selected[0])">Edit</button>
      }
      <button mat-button (click)="remove(type, list.selectedOptions.selected)">Remove</button>
    } @else {
      <button mat-flat-button (click)="open(type)">Add</button>
    }
  </p>
</ng-template>
